/**
 * Restaurant type based on GET /api/get-restaurant-info-subdomain response.
 * Contains the most relevant fields for the booking flow.
 */

import type { BeveragePairing } from '@features/booking/types/pairings';

export interface RestaurantCustomStyle {
  mainColor?: string;
  mainColorText?: string;
  mainColorBorder?: string;
  textSubHeaderColor?: string;
  bodyColor?: string | null;
  bodyImage?: string;
  headerColor?: string;
  headerImage?: string;
  subHeaderColor?: string;
  headerButtonsColor?: string;
  faviconImage?: string;
  emailButtonColor?: string;
  emailHeaderImage?: string;
}

export interface RestaurantAppTexts {
  [lang: string]: Record<string, string>;
}

export interface RestaurantMenu {
  id: number;
  name: string;
  description?: string;
  /** Base price string as the backend formats it, undated (read prices through menuPriceLabel) */
  price?: string;
  /** Price as a number (null = à la carte or unparsed legacy text; absent on older backends) */
  priceAmount?: number | null;
  /** ISO 4217 code for priceAmount */
  priceCurrency?: string | null;
  details?: string;
  preferredAccess?: boolean;
  maxPartySizeAllowed?: number;
  minPartySizeAllowed?: number;
  url?: string;
  image?: string;
  showInMainView?: boolean;
  eventMenu?: boolean;
  privateMenu?: boolean;
  marketplace?: boolean;
  isDeleted?: boolean;
  orderPriority?: number;
}

export interface RestaurantHoursAvailable {
  value: string;
  label: string;
  serviceType: string;
  dayOfWeek?: number;
  openTime?: string;
  closeTime?: string;
}

export interface RestaurantPartner {
  id: string;
  name: string;
  subdomainPrefix?: string;
  address?: string;
  address2?: string | null;
  logoUrl?: string;
  customStyle?: {
    headerImage?: string;
    [key: string]: unknown;
  };
}

export interface RestaurantDeliveryZone {
  id: string;
  name: string;
  fee: number;
}

/**
 * Per-locale display values for a public area. `null` means "no translation
 * stored" — the backend never ships an empty-string copy of the canonical
 * value — so a plain `??` fallback to the top-level `name`/`description` is
 * the correct display rule (meitre-backend docs/BOOKING_FLOW_FE_INTEGRATION.md
 * §2, "Note on translations").
 */
export interface PublicAreaTranslation {
  name: string | null;
  description: string | null;
}

export interface PublicArea {
  id: number;
  /** Canonical, always present, never varies by locale. */
  name: string;
  /** Canonical, always present, never varies by locale. */
  description: string;
  priority: number;
  /**
   * Backend ships en/es/pt today; `Record<string, …>` because the read side
   * indexes by an unconstrained runtime locale tag. Optional to tolerate older
   * cached payloads that predate the translations rollout (treat absent as
   * all-null). Nullability lives at the FIELD level — a present locale key
   * always maps to a full entry, per the contract's payload example.
   *
   * Display-only. Matching logic reads the canonical `name` (see
   * utils/areaUtils.ts menuMatchesArea), so localized values must not reach it.
   */
  translations?: Record<string, PublicAreaTranslation | undefined>;
}

export const PUBLIC_SEARCH_MODE = {
  MENU: 'menu',
  AREA: 'area',
  MENU_AREA: 'menu_area',
} as const;

export type PublicSearchMode = (typeof PUBLIC_SEARCH_MODE)[keyof typeof PUBLIC_SEARCH_MODE];

export interface Restaurant {
  // Core identification
  id: number;
  name: string;
  subdomainPrefix: string;

  // Contact & location
  address: string;
  address2: string | null;
  contactEmail: string;
  countryCode: string;
  countryName: string;
  isoCountryCode: string;
  phone: string;
  whatsAppPhoneNumber: string;
  website: string;
  googleMapsPlaceId: string;

  // Display settings
  pageTitle: string;
  designMode: string;
  mailLinkColor: string;

  // Booking availability
  showReservationsFrom: number;
  showReservationsTo: number;
  showReservationsToAdmin: number;
  showReservationsToDate: string | null;
  showReservationsToDateAdmin: string | null;
  showReservationsToPreferred: number;

  // Timezone & locale
  timezone: string;
  defaultLanguage: string;
  hourFormat: number;

  // Payment
  paymentProcessor: string;
  publicKeyPayment: string;
  statementDescription: string;
  currency: string;
  noShowFeePercent: number;
  depositFeePercent: number;
  marketplaceFeePercent: number;
  paymentLinkFeePercent: number;
  settleUsd: boolean;
  processingSalesTax?: number;
  checkoutProcessingFeePercent?: number;
  reservationGuaranteeCurrency?: string;
  specificServiceChargeOptions?: number[];

  // Status
  isActive: boolean;
  inMaintenance: boolean;
  freeTier: boolean;
  activeBilling: boolean;
  subscription: string;

  // Limits
  dailyLimit: number;
  weeklyLimit: number;
  monthlyLimit: number;

  /**
   * Per-tenant flag for the Confirm-step removal rollout (Phase 3 of
   * `docs/plans/remove-confirm-step.md`). When `true`, the commit moves from
   * the Confirm step to the Guarantee/Personalize step. Read once at session
   * start by BookingProvider and frozen into `bookingState.skipConfirmStep`.
   *
   * Optional in the type to tolerate older API responses or restaurants whose
   * payload doesn't yet include the field — BookingProvider treats anything
   * other than literal `true` as `false`.
   *
   * Wire shape (from the BE): camelCase per the API serializer convention.
   * BE-1 schema: `restaurant.booking_skip_confirmation_v2 BOOL NOT NULL DEFAULT 0`.
   */
  bookingSkipConfirmationV2?: boolean;

  // Features flags
  allowWaitingList: boolean;
  allowGratuity: boolean;
  allowReviews: boolean;
  allowGiftCards: boolean;
  allowGiftCardServiceFee: boolean;
  allowExperienceSelection: boolean;
  forceShowMenuSelection: boolean;
  /**
   * REQ-1 of the force-show-area-selection spec: when `true` and
   * `publicSearchMode === 'area'`, the post-timeslot AREA step shows even when
   * a single bookable area would otherwise auto-assign and skip the page.
   *
   * Optional to tolerate API payloads predating the backend PR that adds
   * `restaurant.force_show_area_selection` — an absent field MUST read as
   * `false` (same pattern as `bookingSkipConfirmationV2`).
   */
  forceShowAreaSelection?: boolean;
  /**
   * Per-restaurant opt-in for the PRE-timeslot AREA_FILTER step (booking spec
   * Flow D). Distinct from `publicSearchMode === 'area'`, which drives area
   * *selection* after a slot is picked: a restaurant can select by area
   * without also offering guests a filter up front.
   *
   * Optional to tolerate API payloads predating the backend PR that adds
   * `restaurant.allow_area_filter` — an absent field MUST read as `false`
   * (same pattern as `forceShowAreaSelection`). That default is safe here
   * even though the filter used to be on by default in area mode: the
   * backend migration backfilled `allow_area_filter = 1` for every
   * restaurant already in `area` mode, so any live restaurant carries an
   * explicit value. `false` only describes a stale/cached payload, and there
   * the correct behaviour is the new default rather than the old one.
   */
  allowAreaFilter?: boolean;
  allowAutoAssignTables: boolean;
  allowOccasion: boolean;
  allowServiceLanguage: boolean;
  allowBanner: boolean;
  allowReserveWithGoogle: boolean;
  allowBillPayment: boolean;
  allowFullBillingDetails: boolean;
  allowBillingReceiptType: boolean;
  allowPromoCode: boolean;
  allowTakeaway: boolean;
  allowDelivery: boolean;
  allowNewDesign: boolean;
  allowCompanions: boolean;
  allowSuggestionsForDenials: boolean;
  offerCrossAvailabilityFromPartners?: boolean;
  allowOnlineOrders: boolean;
  allowOnlineOrdersReservation: boolean;
  allowAMEXBR?: boolean;
  /** Server-computed Amex → platform Stripe routing mode; absent on older backends (see amexChange.ts). */
  amexPlatformRouting?: 'all' | 'no_show_only' | 'off';
  blockBookATable: boolean;

  // Personalization flags
  allowAllergies: boolean;
  allowVeggieMenu: boolean;
  allowKidsOption: boolean;
  allowPets: boolean;
  allowAccessibility: boolean;

  // Communication
  emailSendAddress: string;
  smsEnabled: boolean;
  whatsAppEnabled: boolean;
  reservationReminderHours: number;

  // Social & analytics
  instagramAccount: string | null;
  facebookURL: string | null;
  tripAdvisorId: string;
  googleAnalyticsKey: string;
  googleTagManagerKey: string;
  facebookPixelKey: string;

  // Banner
  bannerImage: string;
  bannerURL: string;

  // Waiting queue
  waitingQueueEnabled: boolean;
  blockWaitingListDaysBefore: number;

  // Menu URLs
  menuURL: string | null;
  takeawayMenuURL: string;

  // Timestamps
  createdAt: string;
  updatedAt: string;

  // Complex nested data
  menus: Record<string, RestaurantMenu[]>;
  beveragePairings: BeveragePairing[];
  restaurantGroup: unknown[];
  customStyle: RestaurantCustomStyle;
  hoursAvailable: RestaurantHoursAvailable[];
  serviceTypes: string[];
  appTexts: RestaurantAppTexts;
  partners: RestaurantPartner[];
  eventRecommendations: Record<string, Array<{ text: string; url?: string }>>;
  takeawayServices: string[];
  deliveryServices: string[];
  deliveryZones: RestaurantDeliveryZone[];

  // Other
  manager: string;
  partnerApiKey: string;
  hasMarketplace: boolean;
  availabilityClosedText: string;
  allowedPartySizes: number[] | null;
  maxPartySizeAllowed?: number;
  minPartySizeAllowed?: number;
  allowLargePartySizeRequest?: boolean;
  publicSearchMode: PublicSearchMode;
  publicAreas?: PublicArea[];
  multiServiceFloorplanView: boolean;
  autoLogoutMinutes: number;

  // Allow additional fields from API
  [key: string]: unknown;
}

export interface TenantInfo {
  subdomainPrefix: string;
  apiBaseUrl: string;
}
